import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, TrendingUp, Users, Activity, AlertCircle, Clock, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

export const ClinicPatientRetention = () => {
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  // Chart data
  const departmentRetentionData = [
    { department: 'Pediatrics', rate: 61.8, color: '#10b981' },
    { department: 'Dermatology', rate: 57.1, color: '#3b82f6' },
    { department: 'General Practice', rate: 52.4, color: '#6366f1' },
    { department: 'Physiotherapy', rate: 31.3, color: '#ef4444' }
  ];

  const monthlyNoShowData = [
    { month: 'Jan', rate: 17.2 }, { month: 'Feb', rate: 18.9 }, { month: 'Mar', rate: 19.4 },
    { month: 'Apr', rate: 18.1 }, { month: 'May', rate: 17.8 }, { month: 'Jun', rate: 19.2 },
    { month: 'Jul', rate: 18.6 }, { month: 'Aug', rate: 17.4 }, { month: 'Sep', rate: 18.9 },
    { month: 'Oct', rate: 17.9 }, { month: 'Nov', rate: 18.4 }, { month: 'Dec', rate: 18.1 }
  ];

  const kpiData = [
    { metric: 'First-visit return rate', baseline: 43.0, target: 52.0, unit: '%', icon: TrendingUp, color: 'teal' },
    { metric: 'Overall no-show rate', baseline: 18.3, target: 13.0, unit: '%', icon: Calendar, color: 'blue' },
    { metric: 'Wait time > 45 min', baseline: 28.0, target: 15.0, unit: '%', icon: Clock, color: 'amber' },
    { metric: 'Physiotherapy retention', baseline: 31.3, target: 45.0, unit: '%', icon: Activity, color: 'purple' },
    { metric: 'Patient satisfaction', baseline: 3.6, target: 3.9, unit: '/5', icon: Users, color: 'green' },
    { metric: 'Evening no-show rate', baseline: 23.6, target: 17.0, unit: '%', icon: AlertCircle, color: 'red' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-slate-100 pt-16 md:pt-20">
      <Helmet>
        <title>Clinic Patient Retention Case Study - StatZen Analytics</title>
        <meta name="description" content="How a UAE polyclinic used operational analytics to identify why 57% of patients weren't returning and built a KPI dashboard to track retention." />
        <link rel="canonical" href="https://statzenanalytics.com/case-studies/clinic-patient-retention" />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 via-cyan-700 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button onClick={() => navigate('/case-studies')} className="flex items-center gap-2 text-teal-100 hover:text-white mb-6 transition-colors group">
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Case Studies
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Badge className="mb-4 bg-cyan-500 hover:bg-cyan-600 text-white border-0">
                Clinical Analytics · Patient Retention · Performance Monitoring · Polyclinic Analytics
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
                Why Patients Don&apos;t Come Back: A Data-Driven Approach to Clinic Patient Retention
              </h1>
              <p className="text-lg sm:text-xl text-teal-100 mb-6 leading-relaxed max-w-4xl">
                How a UAE multi-specialty clinic used operational analytics to identify why 57% of new patients weren&apos;t returning. and built a KPI dashboard to track and reverse it.
              </p>
            </div>
          </div>

          <div className="mt-6 bg-yellow-500/20 border border-yellow-300/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-200 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-50 font-medium">Simulated Data for Demonstration</p>
                <p className="text-xs text-yellow-100 mt-1">
                  All patient visit data, operational metrics, and analytical outputs shown are synthetically generated for portfolio demonstration purposes. Methods and findings are illustrative of real-world clinic analytics engagements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* At a Glance */}
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">At a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The Situation</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  A UAE multi-specialty clinic was seeing strong new patient volume but losing 57% of first-time patients. who never returned for a second visit. Nobody knew why.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What We Did</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Analysed 1,200 patient visits across 4 departments over 12 months. mapping no-show rates, wait times, return visit conversion, and satisfaction drivers. then built a KPI dashboard for ongoing monitoring.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-amber-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What It Showed</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Patients who waited more than 45 minutes on their first visit had 67% lower odds of returning. An 18.3% no-show rate was costing the clinic approximately 220 appointment slots per month. Both problems were invisible without structured analytics.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* The Situation */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur border-l-4 border-teal-600">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Situation</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                A UAE multi-specialty clinic operating across four departments. General Practice, Dermatology, Physiotherapy, and Pediatrics. was facing a problem that is common across private healthcare in the UAE but rarely measured with precision: patients were coming once and not returning.
              </p>
              <p>
                New patient acquisition was healthy. Monthly visit volume was growing. But the clinic&apos;s revenue per patient was not keeping pace. because the majority of new patients were not converting into repeat patients. The clinic was spending on marketing and referrals to fill appointment books with patients it was then losing after a single visit.
              </p>
              <p>
                The management team had three theories about why patients weren&apos;t returning: waiting times were too long, the booking process was difficult, or patients were simply trying different clinics. They had no data to know which theory was correct. or whether all three were true simultaneously.
              </p>
              <p className="font-semibold text-gray-900">Two questions drove this engagement:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Which operational and clinical factors are most strongly associated with whether a patient returns after their first visit?</li>
                <li>Which specific departments, time slots, and patient profiles are driving the no-show and non-return problem. and what does fixing it look like?</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Business Problem */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Business Problem</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed mb-6">
              <p>
                Patient retention is the most underleveraged commercial lever in UAE private healthcare. Every clinic tracks new patient numbers. Very few track what happens after the first visit.
              </p>
              <p>
                The economics are straightforward. A new patient costs significantly more to acquire than an existing patient costs to retain. A patient who visits once and doesn&apos;t return represents the full acquisition cost with none of the lifetime value. At this clinic:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>1,200 patient visits over 12 months across all departments</li>
                <li>43% first-visit-to-return conversion rate overall. meaning 57% of new patients did not return</li>
                <li>18.3% no-show rate. approximately 220 appointment slots per month sitting empty after being booked</li>
                <li>Average OPD wait time 32 minutes. but 28% of patients waiting longer than 45 minutes</li>
                <li>Overall patient satisfaction score 3.6 out of 5. with significant variation across departments</li>
              </ul>
            </div>

            <Card className="border-2 border-amber-400 bg-amber-50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The retention calculation every clinic owner understands:</h3>
                <p className="text-gray-700 leading-relaxed">
                  If 100 new patients visit each month and only 43 return, you are losing 57 potential repeat patients every month. If the average repeat patient visits 3–4 times per year at an average consultation value of AED 250–400, each percentage point improvement in retention rate compounds into meaningful annual revenue. The question is not whether retention matters. it is why it is not being measured.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Where Problem Was Happening - content continues in next file segment */}

        {/* Where Problem Was Happening */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Where Patient Retention Was Breaking Down</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              The analysis began by mapping retention and no-show rates across every dimension available in the clinic&apos;s data. department, day of week, time slot, patient age group, and wait time category. The goal was to stop treating retention as a clinic-wide problem and identify exactly where it was concentrated.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">First-Visit-to-Return Conversion Rate by Department</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Department</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Total Visits</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">New Patients</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Returned</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Retention Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">General Practice</td>
                    <td className="px-4 py-3 text-sm text-gray-700">412</td>
                    <td className="px-4 py-3 text-sm text-gray-700">187</td>
                    <td className="px-4 py-3 text-sm text-gray-700">98</td>
                    <td className="px-4 py-3 text-sm text-gray-700">52.4%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Dermatology</td>
                    <td className="px-4 py-3 text-sm text-gray-700">318</td>
                    <td className="px-4 py-3 text-sm text-gray-700">156</td>
                    <td className="px-4 py-3 text-sm text-gray-700">89</td>
                    <td className="px-4 py-3 text-sm text-gray-700">57.1%</td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">Physiotherapy</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">284</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">134</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">42</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">31.3%</td>
                  </tr>
                  <tr className="bg-green-50">
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">Pediatrics</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">186</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">89</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">55</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">61.8%</td>
                  </tr>
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-4 py-3 text-sm text-gray-900">Overall</td>
                    <td className="px-4 py-3 text-sm text-gray-900">1,200</td>
                    <td className="px-4 py-3 text-sm text-gray-900">566</td>
                    <td className="px-4 py-3 text-sm text-gray-900">284</td>
                    <td className="px-4 py-3 text-sm text-gray-900">43.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 italic mb-8">
              Physiotherapy retention rate of 31.3% is dramatically below all other departments. and below the clinic average by 11.7 percentage points. This was the first insight the data produced that management did not already know.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">No-Show Rate by Department and Time Slot</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Department</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Morning (8–12)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Afternoon (12–4)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Evening (4–8)</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Overall No-Show</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">General Practice</td>
                    <td className="px-4 py-3 text-sm text-gray-700">12.1%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">19.4%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">22.8%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">17.3%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">Dermatology</td>
                    <td className="px-4 py-3 text-sm text-gray-700">9.8%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">17.2%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">24.1%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">16.2%</td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">Physiotherapy</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">14.3%</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">21.6%</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">28.9%</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">21.2%</td>
                  </tr>
                  <tr className="bg-green-50">
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">Pediatrics</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">8.4%</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">14.7%</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">18.3%</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">13.2%</td>
                  </tr>
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-4 py-3 text-sm text-gray-900">Overall</td>
                    <td className="px-4 py-3 text-sm text-gray-900">11.0%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">18.4%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">23.6%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">18.3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 italic mb-8">
              Evening slot no-show rates are consistently and significantly higher than morning slots across all departments. At 23.6% overall, evening appointments are generating more than 1 in 5 empty slots. This pattern was consistent across all 12 months of data.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">OPD Wait Time Distribution</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Wait Time Category</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">% of Patients</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Mean Satisfaction Score</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Return Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="bg-green-50">
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">Under 15 minutes</td>
                    <td className="px-4 py-3 text-sm text-gray-700">18.2%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">4.3/5</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-900">71.4%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">15–30 minutes</td>
                    <td className="px-4 py-3 text-sm text-gray-700">34.1%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">3.9/5</td>
                    <td className="px-4 py-3 text-sm text-gray-700">58.2%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">30–45 minutes</td>
                    <td className="px-4 py-3 text-sm text-gray-700">19.7%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">3.4/5</td>
                    <td className="px-4 py-3 text-sm text-gray-700">41.6%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-3 text-sm font-semibold text-amber-900">45–60 minutes</td>
                    <td className="px-4 py-3 text-sm text-gray-700">16.8%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">2.9/5</td>
                    <td className="px-4 py-3 text-sm font-semibold text-amber-900">28.3%</td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">Over 60 minutes</td>
                    <td className="px-4 py-3 text-sm text-gray-700">11.2%</td>
                    <td className="px-4 py-3 text-sm text-gray-700">2.3/5</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-900">19.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 italic">
              The relationship between wait time and return rate is striking and consistent: patients waiting under 15 minutes return at 71.4%, while patients waiting over 60 minutes return at only 19.1%. a 52.3 percentage point gap driven entirely by how long they waited on their first visit.
            </p>
          </CardContent>
        </Card>

        {/* Statistical Methodology - abbreviated for space */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <p className="text-sm font-semibold text-teal-600 mb-2">THE ANALYSIS</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How We Found It. Statistical Methodology</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Four analytical methods were applied sequentially to move from descriptive patterns to predictive insights to a monitoring system.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Descriptive Analytics:</span>
                    <span className="text-gray-700"> Baseline metrics across departments, time slots, and patient profiles</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Patient Satisfaction Regression:</span>
                    <span className="text-gray-700"> Ranked driver list. wait time β=0.38 strongest predictor</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Logistic Regression:</span>
                    <span className="text-gray-700"> Odds ratios for return visit prediction. wait time, department, satisfaction</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">KPI Dashboard:</span>
                    <span className="text-gray-700"> Interactive monitoring with monthly trends and alert thresholds</span>
                  </div>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* KPI Dashboard */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Monitoring System. Clinic Performance KPI Dashboard</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Identifying the problem is the first step. The second step is building a system that ensures the clinic can track whether interventions are working. and catch new retention problems before they compound.
            </p>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {kpiData.map((kpi, index) => {
                const Icon = kpi.icon;
                const improvement = ((kpi.target - kpi.baseline) / kpi.baseline * 100).toFixed(1);
                return (
                  <Card key={index} className={`border-2 border-${kpi.color}-200 hover:shadow-md transition-shadow`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Icon className={`w-5 h-5 text-${kpi.color}-600`} />
                        <Badge className={`bg-${kpi.color}-100 text-${kpi.color}-700 text-xs`}>
                          {improvement > 0 ? '+' : ''}{improvement}%
                        </Badge>
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">{kpi.metric}</h4>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-2xl font-bold text-gray-900">{kpi.baseline}{kpi.unit}</span>
                        <span className="text-sm text-gray-500">→</span>
                        <span className="text-lg font-semibold text-teal-600">{kpi.target}{kpi.unit}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Target</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">First-Visit Return Rate by Department</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={departmentRetentionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" tick={{ fontSize: 11 }} />
                    <YAxis label={{ value: 'Return Rate (%)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="rate" fill="#14b8a6">
                      {departmentRetentionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Monthly No-Show Rate Trend</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={monthlyNoShowData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis label={{ value: 'No-Show Rate (%)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={2} name="No-Show Rate" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Findings */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Findings</h2>
            <div className="space-y-4">
              {[
                'Wait time is the primary retention driver in UAE polyclinic settings. patients who waited >45 minutes on their first visit had 67% lower odds of returning, independent of clinical quality or satisfaction with the doctor',
                'Physiotherapy retention (31.3%) was dramatically below all other departments and below the clinic average by 11.7 percentage points. a problem entirely invisible at the overall clinic level',
                'Evening appointment slots had a 23.6% no-show rate vs 11.0% for morning slots. a 12.6 percentage point gap generating approximately 220 empty slots monthly',
                'Wait time experience was the strongest predictor of patient satisfaction in the polyclinic setting (β=0.38). ahead of doctor communication (β=0.26). a pattern distinct from hospital settings where clinical quality dominates',
                'Same-day bookings showed 42% higher odds of patient return compared to advance bookings. patients who self-initiate appointments have stronger engagement',
                '28% of patients waited longer than 45 minutes on their first visit. the threshold above which return rate drops below 30%'
              ].map((finding, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 leading-relaxed">{finding}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* What This Delivered */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Delivered</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              The analysis moved the clinic from managing by intuition to managing by evidence. Every intervention is now tied to a measured baseline and a target. and the dashboard tracks whether the interventions are working.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <Card className="border-2 border-red-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Before</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• 43% first-visit return rate. no breakdown by department or driver</li>
                    <li>• Physiotherapy retention problem invisible</li>
                    <li>• 18.3% no-show rate accepted as normal</li>
                    <li>• Wait time complaints handled case by case</li>
                    <li>• Quality budget spent on facility upgrades</li>
                    <li>• No monitoring system</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">After</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Retention tracked by department with specific targets</li>
                    <li>• Physiotherapy identified as priority (31.3% → 45% target)</li>
                    <li>• Evening no-show (23.6%) targeted with reminders → 17%</li>
                    <li>• Wait time >45min identified as retention driver</li>
                    <li>• Quality budget redirected to wait time reduction</li>
                    <li>• KPI dashboard live with monthly review</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-teal-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">The Retention Finding That Changed the Conversation</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    The logistic regression finding. that patients waiting more than 45 minutes have 67% lower odds of returning. shifted the internal quality improvement conversation from &apos;how do we make the clinic look better&apos; to &apos;how do we get patients seen faster.&apos; These are completely different problems with completely different solutions. Data is what made the distinction clear.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Physiotherapy Identified as the Priority Department</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    A 31.3% first-visit return rate in Physiotherapy. against a clinic average of 43% and a Pediatrics rate of 61.8%. would not have been visible without department-level retention analysis. Physiotherapy now has a specific improvement plan, a specific retention target (45% in six months), and a monthly tracking dashboard.
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Services Used in This Engagement</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: '🏥', title: 'Clinical & Healthcare Analytics', desc: 'Patient visit pattern analysis, no-show rate analysis, retention rate tracking by department, OPD wait time analysis, DHA/DOH compliance support.', link: '/services#clinical-analytics' },
                { icon: '📊', title: 'Outcome Optimization', desc: 'Retention root cause analysis, quality improvement strategy with measurable targets, identification of highest-return operational interventions.', link: '/services#outcome-optimization' },
                { icon: '📈', title: 'Performance Monitoring & Dashboards', desc: 'KPI dashboard with monthly trend tracking, department-level filtering, alert thresholds, and actionable reporting for clinic management.', link: '/services#performance-monitoring' },
                { icon: '📋', title: 'Survey & Questionnaire Data Analysis', desc: 'Patient satisfaction survey analysis, reliability testing, regression modelling, ranked driver identification, reporting and visualization.', link: '/services#survey-analysis' }
              ].map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="text-3xl mb-3">{service.icon}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-700 mb-4 leading-relaxed text-sm">{service.desc}</p>
                    <button onClick={() => navigate(service.link)} className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
                      Learn more →
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-teal-50 to-blue-50">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Do You Know Why Your Patients Aren&apos;t Coming Back?
            </h3>
            <p className="text-gray-700 mb-4 max-w-2xl mx-auto">
              Most clinics track new patient volume. Very few track what happens after the first visit. or know which operational factor is most responsible for patients not returning. If you have appointment data and a retention rate you are not happy with, that is exactly where we start.
            </p>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Book a free 30-minute consultation. Bring your appointment data, your no-show rate, and your biggest operational frustration. We will show you what the analysis would tell you. and what fixing it would be worth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/contact')} size="lg" className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8">
                Book a Free Consultation
              </Button>
              <Button onClick={() => navigate('/services')} size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8">
                View Our Healthcare Analytics Services
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default ClinicPatientRetention;
